/**
 * 역할 편집 — 권한 매트릭스(리소스 × 동작 × 범위) 순수 로직.
 *
 * 서버의 권한 행은 `code = <prefix>.<action>[.<scope>]` 꼴이고(`attendance.read.team`,
 * `payroll.calculate`, `system.admin`), 범위는 `conditions.scope` 가 정본이며 없으면 전체(all)다.
 * 한 역할의 실효 범위는 같은 리소스·동작 안에서 **가장 넓은** 범위(self < team < all)라서,
 * 화면은 칸(리소스 × 동작)마다 「없음 / 본인 / 팀 / 전체」 하나를 고르는 꼴로 보여 주고,
 * 고른 범위 위의 행은 빼고 그 범위의 행은 넣는다(아래 행은 이미 골라져 있으면 그대로 둔다).
 *
 * 리소스는 저장된 `resource` 값 그대로 묶는다(`leave` 와 `leave-request`, `education` 과
 * `educationSubsidy` 는 서로 다른 줄). code 의 접두어는 resource 와 다를 수 있어
 * (`leaveRequest.approve` 의 resource 는 `leave-request`) 동작은 접두어를 떼지 않고 토큰 위치로 읽는다.
 */

export type PermissionScope = "self" | "team" | "all";
/** 칸에 고른 값 — "" 는 없음(그 동작의 행을 하나도 안 가짐). */
export type CellScope = PermissionScope | "";
export type MatrixAction = "read" | "write" | "approve" | "delete" | "execute" | "manage";

/** 열 순서(고정) — 조회 · 기록/수정 · 승인 · 삭제 · 실행 · 관리. */
export const MATRIX_ACTIONS: readonly MatrixAction[] = [
  "read",
  "write",
  "approve",
  "delete",
  "execute",
  "manage",
];

/** 범위 오름차순 — 뒤가 더 넓다. */
export const SCOPE_ORDER: readonly PermissionScope[] = ["self", "team", "all"];

/** code 끝 토큰 → 범위. `department` 범위 행은 현재 없지만 들어오면 팀으로 본다. */
const SCOPE_TOKENS: Record<string, PermissionScope> = {
  self: "self",
  team: "team",
  all: "all",
  department: "team",
};

/** code 의 동작 토큰 → 열. 열 이름과 다른 토큰만 적는다. */
const ACTION_ALIASES: Record<string, MatrixAction> = {
  calculate: "execute",
  admin: "manage",
  create: "write",
  update: "write",
};

export interface PermissionRowLike {
  id: string;
  code: string;
  name?: string | null;
  description?: string | null;
  resource: string;
  actions?: string[] | null;
  conditions?: { scope?: string | null } | null;
}

/** 한 칸 — 범위별 권한 행 id. 없는 범위는 키가 없다. */
export type MatrixCell = Partial<Record<PermissionScope, string>>;

export interface MatrixResource {
  resource: string;
  label: string;
  cells: Partial<Record<MatrixAction, MatrixCell>>;
  /** 이 리소스에 속한 행 id 전부(「해제」 가 지울 대상). */
  ids: string[];
}

export interface MatrixRowInfo {
  row: PermissionRowLike;
  action: MatrixAction;
  scope: PermissionScope;
}

export interface PermissionMatrix {
  resources: MatrixResource[];
  byId: Record<string, MatrixRowInfo>;
  /** 행이 하나라도 있는 열만, MATRIX_ACTIONS 순서로. */
  actions: MatrixAction[];
}

export interface MatrixFilter {
  query?: string;
  resource?: string;
  scope?: CellScope;
  selectedOnly?: boolean;
  selectedIds?: readonly string[];
  /** 리소스별 추가 검색 문구(ko·en 라벨 등) — 키는 resource. */
  labels?: Record<string, string>;
}

export interface CellDiff {
  mine: CellScope;
  other: CellScope;
}

export function cellKey(resource: string, action: MatrixAction): string {
  return `${resource}:${action}`;
}

export function scopeRank(scope: CellScope): number {
  return scope === "" ? -1 : SCOPE_ORDER.indexOf(scope);
}

/** code 토큰에서 동작·범위를 읽는다. 첫 토큰(접두어)은 리소스라 건너뛴다. */
export function parsePermissionCode(code: string): {
  action: MatrixAction | null;
  scope: PermissionScope | null;
} {
  const tokens = code.split(".").slice(1);
  let scope: PermissionScope | null = null;
  const last = tokens[tokens.length - 1];
  if (tokens.length > 1 && last && SCOPE_TOKENS[last]) {
    scope = SCOPE_TOKENS[last];
    tokens.pop();
  }
  const token = tokens[0];
  if (!token) {
    return { action: null, scope };
  }
  const aliased = ACTION_ALIASES[token];
  if (aliased) {
    return { action: aliased, scope };
  }
  return {
    action: (MATRIX_ACTIONS as readonly string[]).includes(token) ? (token as MatrixAction) : null,
    scope,
  };
}

/** 범위 — conditions.scope 가 정본, 없으면 code 끝 토큰, 그것도 없으면 전체. */
export function rowScope(row: PermissionRowLike): PermissionScope {
  const fromConditions = row.conditions?.scope;
  if (fromConditions && SCOPE_TOKENS[fromConditions]) {
    return SCOPE_TOKENS[fromConditions];
  }
  return parsePermissionCode(row.code).scope ?? "all";
}

/** 주 동작 — code 토큰이 우선, 못 읽으면 actions 배열에서 가장 넓은 동작(manage > … > read). */
export function rowAction(row: PermissionRowLike): MatrixAction {
  const parsed = parsePermissionCode(row.code).action;
  if (parsed) {
    return parsed;
  }
  const actions = row.actions ?? [];
  for (const candidate of [...MATRIX_ACTIONS].reverse()) {
    if (actions.includes(candidate)) {
      return candidate;
    }
  }
  return "read";
}

export function buildPermissionMatrix(
  rows: readonly PermissionRowLike[],
  labelFor: (resource: string) => string = (r) => r,
): PermissionMatrix {
  const byResource = new Map<string, MatrixResource>();
  const byId: Record<string, MatrixRowInfo> = {};
  const seenActions = new Set<MatrixAction>();

  for (const row of rows) {
    const resource = row.resource || "";
    let entry = byResource.get(resource);
    if (!entry) {
      entry = { resource, label: labelFor(resource) || resource, cells: {}, ids: [] };
      byResource.set(resource, entry);
    }
    const action = rowAction(row);
    const scope = rowScope(row);
    byId[row.id] = { row, action, scope };
    entry.ids.push(row.id);
    seenActions.add(action);
    const cell = entry.cells[action] ?? {};
    // 같은 리소스·동작·범위가 둘이면 먼저 온 행이 칸을 가진다(결정적) — 현재 데이터엔 없다.
    if (!cell[scope]) {
      cell[scope] = row.id;
    }
    entry.cells[action] = cell;
  }

  return {
    resources: [...byResource.values()],
    byId,
    actions: MATRIX_ACTIONS.filter((a) => seenActions.has(a)),
  };
}

/** 칸에 고른 범위 — 선택된 행 중 가장 넓은 범위, 없으면 "". */
export function cellScope(selectedIds: readonly string[], cell: MatrixCell | undefined): CellScope {
  if (!cell) {
    return "";
  }
  for (const scope of [...SCOPE_ORDER].reverse()) {
    const id = cell[scope];
    if (id && selectedIds.includes(id)) {
      return scope;
    }
  }
  return "";
}

/** 칸에서 고를 수 있는 가장 넓은 범위("" 면 행이 없는 칸). */
export function cellMaxScope(cell: MatrixCell | undefined): CellScope {
  if (!cell) {
    return "";
  }
  for (const scope of [...SCOPE_ORDER].reverse()) {
    if (cell[scope]) {
      return scope;
    }
  }
  return "";
}

/**
 * 칸의 범위를 S 로 — S 보다 넓은 행은 빼고 S 행은 넣는다. 더 좁은 행은 이미 골라져 있으면 그대로
 * 둔다(실효 범위는 가장 넓은 것이라 결과가 같고, 좁은 행을 굳이 지워 다른 화면의 기대를 깨지 않는다).
 * "" 는 칸의 행을 전부 뺀다. 칸에 없는 범위를 고르면 넓은 행만 빼고 끝난다.
 */
export function applyCellScope(
  selectedIds: readonly string[],
  cell: MatrixCell | undefined,
  scope: CellScope,
): string[] {
  if (!cell) {
    return [...selectedIds];
  }
  const target = scopeRank(scope);
  const remove = new Set<string>();
  for (const s of SCOPE_ORDER) {
    const id = cell[s];
    if (id && scopeRank(s) > target) {
      remove.add(id);
    }
  }
  const next = selectedIds.filter((id) => !remove.has(id));
  if (scope !== "") {
    const add = cell[scope];
    if (add && !next.includes(add)) {
      next.push(add);
    }
  }
  return next;
}

/** 리소스의 모든 칸을 가장 넓은 범위로. */
export function applyResourceMax(
  selectedIds: readonly string[],
  resource: MatrixResource,
): string[] {
  let next = [...selectedIds];
  for (const action of MATRIX_ACTIONS) {
    const cell = resource.cells[action];
    if (cell) {
      next = applyCellScope(next, cell, cellMaxScope(cell));
    }
  }
  return next;
}

/** 리소스의 행을 전부 뺀다(없음). */
export function clearResource(selectedIds: readonly string[], resource: MatrixResource): string[] {
  const drop = new Set(resource.ids);
  return selectedIds.filter((id) => !drop.has(id));
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, "");
}

function resourceSearchText(
  resource: MatrixResource,
  matrix: PermissionMatrix,
  labels?: Record<string, string>,
): string {
  const parts: string[] = [resource.resource, resource.label, labels?.[resource.resource] ?? ""];
  for (const id of resource.ids) {
    const row = matrix.byId[id]?.row;
    if (row) {
      parts.push(row.code, row.name ?? "", row.description ?? "");
    }
  }
  return normalize(parts.join(" "));
}

/** 필터 — 리소스(줄) 단위로 거른다. 칸은 그대로 둬 열이 흔들리지 않게 한다. */
export function filterMatrix(matrix: PermissionMatrix, filter: MatrixFilter): PermissionMatrix {
  const query = normalize(filter.query ?? "");
  const selected = filter.selectedIds ?? [];
  const resources = matrix.resources.filter((res) => {
    if (filter.resource && res.resource !== filter.resource) {
      return false;
    }
    if (query && !resourceSearchText(res, matrix, filter.labels).includes(query)) {
      return false;
    }
    if (filter.selectedOnly && !res.ids.some((id) => selected.includes(id))) {
      return false;
    }
    if (filter.scope) {
      const hit = MATRIX_ACTIONS.some((a) => cellScope(selected, res.cells[a]) === filter.scope);
      if (!hit) {
        return false;
      }
    }
    return true;
  });
  return { resources, byId: matrix.byId, actions: matrix.actions };
}

/** 두 선택의 칸별 차이 — 고른 범위가 다른 칸만 `resource:action` 키로. */
export function diffAgainst(
  selectedIds: readonly string[],
  otherIds: readonly string[],
  matrix: PermissionMatrix,
): Record<string, CellDiff> {
  const diff: Record<string, CellDiff> = {};
  for (const res of matrix.resources) {
    for (const action of MATRIX_ACTIONS) {
      const cell = res.cells[action];
      if (!cell) {
        continue;
      }
      const mine = cellScope(selectedIds, cell);
      const other = cellScope(otherIds, cell);
      if (mine !== other) {
        diff[cellKey(res.resource, action)] = { mine, other };
      }
    }
  }
  return diff;
}

/* ── 기능별 필수 권한 묶음(MED-989) — 정본은 @hereby/core/constants FEATURE_PERMISSION_BUNDLES ── */

export interface BundleRequirementLike {
  resource: string;
  action: MatrixAction;
  scope: PermissionScope;
}

export type BundleStatus = "full" | "partial" | "none";

export interface BundleCheck {
  status: BundleStatus;
  /** 아직 범위가 모자란 요구 */
  missing: BundleRequirementLike[];
  /** 매트릭스(카탈로그)에 그 범위 이상의 행이 아예 없어 이 화면에서 줄 수 없는 요구 */
  unavailable: BundleRequirementLike[];
}

function findCell(matrix: PermissionMatrix, r: BundleRequirementLike): MatrixCell | undefined {
  return matrix.resources.find((res) => res.resource === r.resource)?.cells[r.action];
}

/** 칸에서 `min` 이상인 범위 중 가장 좁은 것 — 없으면 "". 요구 범위 행이 없으면 더 넓은 행으로 채운다. */
export function narrowestScopeAtLeast(
  cell: MatrixCell | undefined,
  min: PermissionScope,
): CellScope {
  if (!cell) {
    return "";
  }
  for (const scope of SCOPE_ORDER) {
    if (scopeRank(scope) >= scopeRank(min) && cell[scope]) {
      return scope;
    }
  }
  return "";
}

/** 역할이 묶음을 얼마나 갖췄나 — 요구마다 현재 범위가 최소 범위 이상이면 충족. */
export function checkBundle(
  selectedIds: readonly string[],
  matrix: PermissionMatrix,
  requires: readonly BundleRequirementLike[],
): BundleCheck {
  const missing: BundleRequirementLike[] = [];
  const unavailable: BundleRequirementLike[] = [];
  for (const r of requires) {
    const cell = findCell(matrix, r);
    if (scopeRank(cellScope(selectedIds, cell)) >= scopeRank(r.scope)) {
      continue;
    }
    if (narrowestScopeAtLeast(cell, r.scope) === "") {
      unavailable.push(r);
    } else {
      missing.push(r);
    }
  }
  const lacking = missing.length + unavailable.length;
  return {
    status: lacking === 0 ? "full" : lacking === requires.length ? "none" : "partial",
    missing,
    unavailable,
  };
}

/**
 * 묶음을 더한다 — 모자란 칸만 최소 범위(그 범위 행이 없으면 바로 위 범위)로 올리고, 이미 같거나 더 넓은
 * 칸은 건드리지 않는다. 카탈로그에 없는 요구는 건너뛴다(checkBundle 의 unavailable 로 알린다).
 */
export function applyBundle(
  selectedIds: readonly string[],
  matrix: PermissionMatrix,
  requires: readonly BundleRequirementLike[],
): string[] {
  let next = [...selectedIds];
  for (const r of requires) {
    const cell = findCell(matrix, r);
    if (scopeRank(cellScope(next, cell)) >= scopeRank(r.scope)) {
      continue;
    }
    const target = narrowestScopeAtLeast(cell, r.scope);
    if (target === "") {
      continue;
    }
    next = applyCellScope(next, cell, target);
  }
  return next;
}

/**
 * 묶음 체크 해제 — 그 묶음이 필요로 하는 칸을 내린다. 단,
 * - 다른 체크된 묶음(keepRequires)이 같은 칸을 필요로 하면 그 범위까지만 내리고,
 * - 이번에 손으로 바꾼 칸(manualKeys, cellKey 목록)은 건드리지 않으며,
 * - 이미 그보다 낮은 칸은 올리지 않는다.
 */
export function removeBundle(
  selectedIds: readonly string[],
  matrix: PermissionMatrix,
  requires: readonly BundleRequirementLike[],
  keepRequires: readonly BundleRequirementLike[],
  manualKeys: readonly string[],
): string[] {
  let next = [...selectedIds];
  const manual = new Set(manualKeys);
  for (const r of requires) {
    if (manual.has(cellKey(r.resource, r.action))) {
      continue;
    }
    const cell = findCell(matrix, r);
    if (!cell) {
      continue;
    }
    // 남는 묶음이 이 칸에 요구하는 가장 넓은 최소 범위 → 그 범위를 채우는 가장 좁은 행
    let floorMin: PermissionScope | null = null;
    for (const k of keepRequires) {
      if (k.resource === r.resource && k.action === r.action) {
        if (floorMin === null || scopeRank(k.scope) > scopeRank(floorMin)) {
          floorMin = k.scope;
        }
      }
    }
    const floor: CellScope = floorMin ? narrowestScopeAtLeast(cell, floorMin) : "";
    if (scopeRank(cellScope(next, cell)) > scopeRank(floor)) {
      next = applyCellScope(next, cell, floor);
    }
  }
  return next;
}

/* ── 리소스 그룹 — 표를 화면 메뉴 기준 묶음으로 접어 보인다. 표에 없는 리소스는 「기타」로(새 리소스가 사라지지 않게). ── */

export interface PermissionGroupDef {
  id: string;
  label: string;
  resources: readonly string[];
}

export const PERMISSION_GROUPS: readonly PermissionGroupDef[] = [
  {
    id: "hr",
    label: "인사 · 조직",
    resources: [
      "organization",
      "ward",
      "role",
      "certification",
      "employee",
      "team",
      "resignation",
      "education",
      "educationSubsidy",
    ],
  },
  {
    id: "attendance",
    label: "근태 · 휴가",
    resources: [
      "attendance",
      "overtime",
      "offsiteWork",
      "leave",
      "leave-request",
      "annualLeavePromotion",
      "schedule",
      "nurse-scheduler",
    ],
  },
  {
    id: "payroll",
    label: "급여",
    resources: [
      "payroll",
      "allowance",
      "deduction",
      "salaryGrade",
      "incentive",
      "fixedIncentive",
      "yearEndSettlement",
    ],
  },
  {
    id: "work",
    label: "업무 · 문서",
    resources: ["document", "generalAffairs", "inventory", "teamDrive", "storage"],
  },
  { id: "comm", label: "소통", resources: ["conversation", "message", "notification", "csReport"] },
  { id: "system", label: "앱 · 시스템", resources: ["ai", "aiApp", "setting", "system"] },
];

export const OTHER_PERMISSION_GROUP = { id: "other", label: "기타" } as const;

export interface PermissionGroup {
  id: string;
  label: string;
  resources: MatrixResource[];
}

/** 리소스를 고정 그룹 순서로 묶고, 그룹 안은 화면 라벨 가나다순. 빈 그룹은 뺀다. */
export function groupMatrixResources(resources: readonly MatrixResource[]): PermissionGroup[] {
  const groupOf = new Map<string, string>();
  for (const g of PERMISSION_GROUPS) {
    for (const r of g.resources) {
      groupOf.set(r, g.id);
    }
  }
  const defs = [...PERMISSION_GROUPS, { ...OTHER_PERMISSION_GROUP, resources: [] }];
  return defs
    .map((g) => ({
      id: g.id,
      label: g.label,
      resources: resources
        .filter((r) => (groupOf.get(r.resource) ?? OTHER_PERMISSION_GROUP.id) === g.id)
        .sort((a, b) => a.label.localeCompare(b.label, "ko")),
    }))
    .filter((g) => g.resources.length > 0);
}
